// @flow

import { useEffect } from "react";
import { publishMqtt, subscribeMqtt } from "./MqttClient";

const delim = "^|";

// presence는 MQTT pulse 기반이다. 만료 기준을 pulse 주기보다 충분히 길게 잡아야
// pulse 몇 번이 늦거나 유실돼도 접속 중인 사용자가 목록에서 사라지지 않는다.
// 특히 백그라운드 탭에서는 브라우저가 setInterval을 1분 이상으로 늦추므로,
// 만료 기준은 그 throttling 주기보다도 길어야 한다.
export const USER_PULSE_INTERVAL_MS = 15 * 1000;
export const USER_PULSE_EXPIRY_MS = 90 * 1000;

export async function publishUserJoined(
  sessionId: string,
  username: string,
  isRepublish: boolean
) {
  if (username === "" || username == null) {
    return;
  }
  await publishMqtt(
    _getSessionUsersTopic(sessionId),
    "joined" + delim + username,
    "SessionUsers.publishUserJoined"
  );
  if (!isRepublish) {
    await publishMqtt(
      _getSessionUsersTopic(sessionId),
      "request_connected_users",
      "SessionUsers.publishUserJoined"
    );
  }
}

// 접속 상태를 주기적으로 알린다. deps에 userContext 같은 객체를 받지 않고
// 문자열만 받으므로, 객체 identity가 바뀌어도 pulse 타이머가 리셋되지 않는다.
export function useUserPresence(sessionId: string, username: string) {
  useEffect(() => {
    if (username === "" || username == null) {
      return;
    }

    publishUserJoined(sessionId, username, false);

    const intervalId = setInterval(() => {
      publishUserJoined(sessionId, username, true);
    }, USER_PULSE_INTERVAL_MS);

    // 백그라운드 탭에서는 pulse가 늦어지므로, 탭이 다시 보이는 즉시 한 번 보낸다
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        publishUserJoined(sessionId, username, true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sessionId, username]);
}

export function subscribeUserList(
  sessionId: string,
  username: string,
  setUsersFunc: any
): any {
  return subscribeMqtt(_getSessionUsersTopic(sessionId), {
    context: "SessionUsers.subscribeUserList",
    next: (data) =>
      _parseMessageAndUpdateUsers(
        sessionId,
        username,
        data.value,
        setUsersFunc
      ),
    // 끊긴 사이 목록이 비었을 수 있으니, 다시 붙으면 접속자들에게 즉시 알려 달라고 요청한다
    onResubscribed: () => {
      publishUserJoined(sessionId, username, false);
    },
  });
}

export function getSessionUsernameTopic(
  sessionId: string,
  username: string
): string {
  return sessionId + delim + username;
}

function _getSessionUsersTopic(sessionId: string): string {
  return sessionId + "_users";
}

function _parseMessageAndUpdateUsers(
  sessionId,
  selfUserName,
  message,
  setUsersFunc
) {
  const [action, username] = message.split(delim);

  if (action === "joined") {
    if (username === selfUserName || username === "" || username == null) {
      return;
    }
    setUsersFunc((previousUsers) => {
      const newUsers = [];
      let exists = false;
      previousUsers.forEach((user) => {
        // Refresh pulse timestamp if this join
        // matches the username already in the array
        if (user.username === username) {
          exists = true;
        }
        newUsers.push({
          username: user.username,
          lastPulse: user.username === username ? Date.now() : user.lastPulse,
        });
      });
      if (!exists) {
        newUsers.push({ username, lastPulse: Date.now() });
      }
      return newUsers;
    });
  } else if (action === "request_connected_users") {
    _resend_connected(sessionId, selfUserName);
  }
}

async function _resend_connected(sessionId, username) {
  await publishMqtt(
    _getSessionUsersTopic(sessionId),
    "joined" + delim + username,
    "SessionUsers.resendConnected"
  );
}
